import Select from "antd/lib/select";
import React, { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Transforms, Editor } from "slate";
import { useSlateStatic, ReactEditor, RenderElementProps } from "slate-react";

import { Logout } from "@sol/uikit/core/icons/Logout";

import { CodeMirrorBase } from "./CodeMirrorBase";
import { CodeElement } from "../editor/types";

interface CodeMirrorEditorProps {
    attributes: RenderElementProps["attributes"];
    children: RenderElementProps["children"];
    element: CodeElement;
}

const SUPPORTED_LANGUAGES: Array<{ value: string; label: string }> = [
    { value: "text", label: "Texte brut" },
    { value: "javascript", label: "JavaScript" },
    { value: "typescript", label: "TypeScript" },
    { value: "python", label: "Python" },
    { value: "sql", label: "SQL" },
    { value: "java", label: "Java" },
    { value: "css", label: "CSS" },
    { value: "html", label: "HTML" },
    { value: "xml", label: "XML" },
    { value: "markdown", label: "Markdown" },
    { value: "shell", label: "Shell" },
    { value: "yaml", label: "YAML" },
    { value: "php", label: "PHP" },
    { value: "ruby", label: "Ruby" },
];

export const CodeMirrorEditor: React.FC<CodeMirrorEditorProps> = ({ attributes, children, element }) => {
    const editor = useSlateStatic();
    const { t } = useTranslation();

    // Get the code text from Slate
    const firstChild = element.children[0];
    const slateText = firstChild && "text" in firstChild ? firstChild.text : "";

    const [localText, setLocalText] = useState(slateText);
    const [currentLanguage, setCurrentLanguage] = useState(element.language || "text");

    // Synchronize local state when Slate changes
    useEffect(() => {
        setLocalText(slateText);
    }, [slateText]);

    const syncWithSlate = useCallback(() => {
        try {
            const path = ReactEditor.findPath(editor, element);

            Editor.withoutNormalizing(editor, () => {
                Transforms.delete(editor, { at: [...path, 0] });
                Transforms.insertNodes(editor, { text: localText }, { at: [...path, 0] });
            });
        } catch {}
    }, [editor, element, localText]);

    // Synchronize Slate with debounce when text changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (localText !== slateText) {
                syncWithSlate();
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [localText, slateText, syncWithSlate]);

    const handleLanguageChange = useCallback(
        (newLanguage: string) => {
            setCurrentLanguage(newLanguage);

            const path = ReactEditor.findPath(editor, element);
            Transforms.setNodes(editor, { language: newLanguage }, { at: path });
        },
        [editor, element],
    );

    const handleBlur = useCallback(() => {
        if (localText !== slateText) {
            syncWithSlate();
        }
    }, [localText, slateText, syncWithSlate]);

    const handleConvertToText = useCallback(() => {
        const path = ReactEditor.findPath(editor, element);

        if (localText !== slateText) {
            syncWithSlate();
        }

        setTimeout(() => {
            Transforms.setNodes(
                editor,
                {
                    type: "paragraph",
                },
                { at: path },
            );
        }, 100);
    }, [editor, element, localText, slateText, syncWithSlate]);

    return (
        <CodeMirrorBase
            value={localText}
            language={currentLanguage}
            readOnly={false}
            onBeforeChange={(editor, data, value) => {
                setLocalText(value);
            }}
            onBlur={handleBlur}
            {...attributes}
        >
            <div className="flex justify-end gap-2 border-t px-3 py-2!">
                <div className="hidden">{children}</div> {/* Mandatory for SlateJS */}
                <Select
                    value={currentLanguage}
                    onChange={handleLanguageChange}
                    options={SUPPORTED_LANGUAGES}
                    aria-label={t("inputs.RichText.CodeMirrorSelectLanguage")}
                    className="w-[120px]!"
                />
                <Logout
                    onClick={handleConvertToText}
                    className="color-red cursor-pointer px-2 py-1"
                    contentEditable={false}
                    title={t("inputs.RichText.CodeMirrorConvertToText")}
                    role="button"
                    aria-label={t("inputs.RichText.CodeMirrorConvertToText")}
                />
            </div>
        </CodeMirrorBase>
    );
};

export default CodeMirrorEditor;
